import { Activity, useState, type ReactNode } from 'react';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import { useLocation, type Location } from 'react-router-dom';
import { getScanModalBackground } from '../../scan/scanModalNavigation';
import { MobileRouteTransition } from './MobileRouteTransition';
import { useMobileShellBottomPadding } from './MobileShellPaddingContext';
import {
  mobileTabIdForPath,
  type MobileTabId,
} from './mobileTabStacks';

type Props = {
  children: (location: Location) => ReactNode;
};

function sameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search && a.key === b.key;
}

/**
 * On mobile, keep each visited primary bottom-nav tab mounted via React
 * `<Activity>`. Tab swaps only toggle visibility (state, DOM, scroll preserved);
 * in-tab push/pop still slide inside the active tab's transition layer.
 *
 * Desktop keeps a single outlet — no need to retain parallel stacks.
 */
export function MobileTabKeepAlive({ children }: Props) {
  const routerLocation = useLocation();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const contentBottomPad = useMobileShellBottomPadding();
  const routeLocation = getScanModalBackground(routerLocation) ?? routerLocation;
  const activeTab = mobileTabIdForPath(routeLocation.pathname);

  const [aliveTabs, setAliveTabs] = useState<Partial<Record<MobileTabId, Location>>>(() =>
    activeTab ? { [activeTab]: routeLocation } : {},
  );

  // Prime / refresh the active tab during render so the first paint after a
  // tab swap never has an empty panel (useEffect would be one frame late).
  let renderTabs = aliveTabs;
  if (isMobile && activeTab) {
    const existing = aliveTabs[activeTab];
    if (!existing || !sameLocation(existing, routeLocation)) {
      renderTabs = { ...aliveTabs, [activeTab]: routeLocation };
      setAliveTabs(renderTabs);
    }
  }

  if (!isMobile) {
    return (
      <MobileRouteTransition>
        {(loc) => children(getScanModalBackground(loc) ?? loc)}
      </MobileRouteTransition>
    );
  }

  const tabIds = Object.keys(renderTabs) as MobileTabId[];

  return (
    <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column" position="relative">
      {tabIds.map((tabId) => {
        const isActive = tabId === activeTab;
        const tabLocation = isActive ? routeLocation : renderTabs[tabId]!;
        return (
          <Activity key={tabId} mode={isActive ? 'visible' : 'hidden'}>
            {/*
              Absolute fill so hidden Activity siblings (display:none) don't
              collapse the shell; the visible panel always owns the viewport.

              Avoid `display: flex` here: page roots become flex items in a
              bounded-height column, and any `overflow: hidden` section
              (e.g. DeckDetailsHeader) can flex-shrink to ~1px while taller
              overflow:visible siblings keep their content height.

              In-flow `pb` on the inner wrapper (not the absolute panel) extends
              window-scroll height so the last rows clear the fixed tab bar —
              AppLayout shell padding alone cannot, because absolute children
              do not grow the document.
            */}
            <Box position="absolute" inset={0} minH={0} minW={0} overflowX="clip">
              <Box minH="100%" pb={contentBottomPad}>
                <MobileRouteTransition location={tabLocation} transitionsEnabled={isActive}>
                  {children}
                </MobileRouteTransition>
              </Box>
            </Box>
          </Activity>
        );
      })}

      {activeTab === null ? (
        <MobileRouteTransition>
          {(loc) => children(getScanModalBackground(loc) ?? loc)}
        </MobileRouteTransition>
      ) : null}
    </Box>
  );
}
